/**
 * Normalize POST /searchFlights JSON body: `deal` may be one object or an array (xmltodict).
 * @param {Record<string, unknown>|null|undefined} data
 * @returns {object[]}
 */
export function dealsListFromSearchFlightsResponseBody(data) {
  if (!data || data.deal == null) return [];
  const d = data.deal;
  return Array.isArray(d) ? d : [d];
}

/**
 * @param {{ status: string, value?: { data?: unknown }, reason?: unknown }} settled
 * @param {string} logLabel
 */
export function dealsFromSearchFlightsSettled(settled, logLabel) {
  if (settled?.status === "fulfilled") {
    return dealsListFromSearchFlightsResponseBody(settled.value?.data);
  }
  if (process.env.NODE_ENV === "development") {
    console.warn(`[parallel flight search] branch "${logLabel}" rejected:`, settled?.reason);
  }
  return [];
}

/**
 * Axios response from performPostFetch: success response or error response shape; network errors reject before this.
 * @param {{ status?: number, data?: unknown }|null|undefined} res
 */
export function isSearchFlightsAxiosResponseOk(res) {
  if (res == null) return false;
  const st = res.status;
  if (typeof st === "number" && (st < 200 || st >= 300)) return false;
  const d = res.data;
  if (d != null && typeof d === "object" && d.status === "fail") return false;
  return true;
}

/**
 * @param {Array<{ status: string, value?: { status?: number, data?: unknown }|null }>} settledResults
 */
export function parallelRoundTripSettledTransportOk(settledResults) {
  if (!Array.isArray(settledResults) || settledResults.length !== 3) return false;
  return settledResults.every(
    (s) => s.status === "fulfilled" && isSearchFlightsAxiosResponseOk(s.value)
  );
}

/**
 * Prefer RT deal for legacy `availableFlights`; if RT empty, use first OW response that has a deal node.
 * @param {Array<{ status: string, value?: { data?: { deal?: unknown } } }>} settledResults — order: RT, outbound OW, inbound OW
 */
function positiveFareNumber(x) {
  const n = Number(x);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * מפתח מיון ל-deal מ-LDS: עדיפות ל-adultFare (מחיר לאדם), אחרת totalFare —
 * תואם ל־buildFlightOfferPricingDisplay ב-flightOfferAdapter.
 * @param {Record<string, unknown>|null|undefined} deal
 */
export function ldsDealPerPersonSortKey(deal) {
  const adult = positiveFareNumber(deal?.dealFare?.adultFare);
  if (adult != null) return adult;
  const total = positiveFareNumber(deal?.dealFare?.totalFare);
  if (total != null) return total;
  return Number.POSITIVE_INFINITY;
}

/**
 * עותק ממוין מהזול ליקר לפי מחיר לאדם (או total כשאין adultFare).
 * @param {object[]|null|undefined} deals
 * @returns {object[]}
 */
export function sortFlightDealsByPerPersonAscending(deals) {
  if (!Array.isArray(deals) || deals.length === 0) return [];
  if (deals.length === 1) return [...deals];
  return [...deals].sort((a, b) => {
    const d = ldsDealPerPersonSortKey(a) - ldsDealPerPersonSortKey(b);
    if (d !== 0) return d;
    const idA = String(a?.dealId ?? a?.ID ?? "");
    const idB = String(b?.dealId ?? b?.ID ?? "");
    return idA.localeCompare(idB);
  });
}

export function pickAvailableFlightsDealFromParallelSettled(
  settledResults,
  rtDealsLen,
  outDealsLen,
  inDealsLen
) {
  const dealNode = (idx) =>
    settledResults[idx]?.status === "fulfilled"
      ? settledResults[idx].value?.data?.deal
      : null;
  if (rtDealsLen > 0 && dealNode(0)) return dealNode(0);
  if (outDealsLen > 0 && dealNode(1)) return dealNode(1);
  if (inDealsLen > 0 && dealNode(2)) return dealNode(2);
  return null;
}
